<template>
  <div>
    <div class="content">
      <form>
        <div class="img-upload-section">
          <b-field
            class="file is-primary file-upload-csr"
            :class="{ 'has-name': !!gift.img_tmp }"
          >
            <b-upload
              v-model="fileImgUploaded"
              class="file-label"
              @input="uploadGiftImage"
              accept="image/png,image/jpeg,image/jpg"
            >
              <span class="file-cta-csr">
                <img
                  v-if="gift.id || fileImgUploaded"
                  :src="editImage"
                  class="edit-gift-image"
                />
                <img :src="url || uploadImage" class="gift-image" />
              </span>
            </b-upload>
          </b-field>
          <span
            v-if="validation.hasError('fileImgUploaded')"
            class="error-validator input-error"
            style="justify-content: center"
          >
            {{ validation.firstError('fileImgUploaded') }}
          </span>
          <div class="img-upload-subtitle">
            Extensões aceitas: jpg, jpeg e png. Máximo: 10 MB.
          </div>
        </div>
        <div class="content-section">
          <div class="input-row">
            <div class="input-label">Nome:</div>
            <b-field
              class="csr-input"
              :message="messageGiftName"
              :type="{ 'is-danger': validation.hasError('gift.nome') }"
            >
              <b-input
                v-model="gift.nome"
                class="input-gift"
                max="75"
                @input="setDraft"
              />
            </b-field>
          </div>

          <div class="input-row">
            <div class="input-label">Categoria:</div>
            <b-field
              :type="{
                'is-danger': validation.hasError('gift.id_categoria'),
              }"
              :message="
                validation.hasError('gift.id_categoria')
                  ? validation.firstError('gift.id_categoria')
                  : ''
              "
            >
              <b-select
                placeholder="Escolha uma categoria"
                v-model="gift.id_categoria"
                class="csr-select"
                @input="setDraft"
              >
                <option
                  v-for="category in giftsListCategories"
                  :key="category.id"
                  :value="category.id"
                >
                  {{ category.nome }}
                </option>
              </b-select>
            </b-field>
          </div>
          <div class="input-row">
            <BaseInputPrice
              @price="updatePrice"
              :initialPrice="parseFloat(data?.valor_final || 0)"
              :hasError="validation.hasError('gift.valor')"
              :isGiftEdition="isGiftEdition"
              :message="validation.firstError('gift.valor')"
            />
          </div>
        </div>
      </form>
    </div>

    <div class="footer">
      <div class="footer-content">
        <button
          :disabled="!hasTouched || hasErrors || isLoadingSaving"
          class="button is-primary"
          :class="{ 'is-loading': isLoadingSaving }"
          @click="submit"
        >
          <span v-if="!isGiftEdition">Criar presente</span>
          <span v-else>Salvar alterações</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import SimpleVueValidator from 'simple-vue-validator'

import GiftsApi from '@/lib/api/Gifts'
import BaseInputPrice from './BaseInputPrice.vue'

import editImage from '@/assets/img/edit-image.svg'
import uploadImage from '@/assets/img/upload_image.png'

const MAX_LENGTH_NAME_GIFTS = 75
const MEGABYTE_IN_BYTES = 1048576
const MAX_SIZE_IMG_UPLOAD_IN_MEGABYTE = 10
const API_LEGACY_URL = process.env.VUE_APP_API_LEGACY_URL
const EMPTY_GIFT = {
  nome: '',
  valor: 0,
  img_tmp: null,
  id_categoria: '',
}

const Validator = SimpleVueValidator.Validator.create({
  templates: {
    required: 'Este campo não pode ficar em branco.',
    float: 'Este campo não pode ficar em branco.',
    maxLength: 'Limite de caracteres atingido.',
  },
})

export default {
  mixins: [SimpleVueValidator.mixin],

  props: ['data'],

  components: {
    BaseInputPrice,
  },

  data() {
    return {
      isLoadingSaving: false,
      gift: EMPTY_GIFT,
      url: null,
      fileImgUploaded: undefined,

      editImage,
      uploadImage,
    }
  },

  computed: {
    ...mapGetters({
      wedId: 'app/getWedId',
      logonToken: 'app/logonToken',
      giftsListCategories: 'gifts/getGiftsListCategories',
    }),

    isGiftEdition() {
      return !!this.gift.id
    },

    messageGiftName() {
      const hasError = this.validation.hasError('gift.nome')
      const errorMessage = this.validation.firstError('gift.nome')
      return (
        (hasError ? `${errorMessage} ` : '') +
        `Caracteres ${this.gift.nome.length}/75`
      )
    },

    hasTouched() {
      return this.validation.passedRecords.length ? true : false
    },

    hasErrors() {
      return (
        this.validation.hasError('gift.nome') ||
        this.validation.hasError('gift.valor') ||
        this.validation.hasError('gift.id_categoria') ||
        this.validation.hasError('gift.img_tmp') ||
        this.validation.hasError('fileImgUploaded')
      )
    },

    notificationAlert: {
      get() {
        return this.$store.state.app.notification
      },
      set(payload) {
        this.$store.dispatch('app/setNotification', payload)
      },
    },
  },

  validators: {
    'gift.nome': (value) => {
      return Validator.value(value).required().maxLength(MAX_LENGTH_NAME_GIFTS)
    },
    'gift.valor': (value) => {
      return Validator.value(value)
        .required()
        .float()
        .custom(() => {
          if (value === 0) return 'Este campo não pode ficar em branco.'
        })
    },
    'gift.id_categoria': (value) => {
      return Validator.value(value).required()
    },
    'gift.img_tmp': function (value) {
      if (!this.isGiftEdition) return Validator.value(value).required()
      return Validator.value(value)
    },
    fileImgUploaded: function (value) {
      if (!this.isGiftEdition)
        return Validator.value(value)
          .custom(() => {
            if (!this.fileImgUploaded) {
              return 'Selecione uma imagem para criar o presente.'
            }
          })
          .custom(() => this.uploadImageIsValid())
      return Validator.value(value).custom(() => this.uploadImageIsValid())
    },
  },

  mounted() {
    if (this.data) {
      this.gift = this.data
      this.url = this.getUrlImage(this.gift)
      this.gift.valor = parseFloat(this.gift.valor_final)
      this.validation.reset()
    } else {
      this.gift = Object.assign({}, this.gift, EMPTY_GIFT)
    }
  },

  methods: {
    ...mapActions({
      fetchGiftsList: 'gifts/fetchGiftsList',
    }),

    updatePrice(data) {
      this.gift = Object.assign({}, this.gift, { valor: data.value })
      if (data.value != 0 && parseFloat(this.gift.valor_final) !== data.value)
        this.setDraft()
    },

    setDraft() {
      localStorage.setItem('drawer-draft', true)
    },

    getUrlImage(gift) {
      if (gift.img_user == '1') {
        return (
          `${API_LEGACY_URL}/dados/sitenoivos/wed${this.wedId}/presentes/` +
          gift.img
        )
      } else {
        return `${API_LEGACY_URL}/img/presentes/` + gift.img
      }
    },

    uploadImageIsValid() {
      if (!this.fileImgUploaded) return false

      const sizeInMB = (this.fileImgUploaded.size || 0) / MEGABYTE_IN_BYTES
      if (sizeInMB > MAX_SIZE_IMG_UPLOAD_IN_MEGABYTE) {
        return 'Ops! A imagem que você inseriu é maior do que limite máximo permitido (10MB). Verifique a sua imagem e tente novamente.'
      }

      switch (this.fileImgUploaded.type) {
        case 'image/png':
        case 'image/jpg':
        case 'image/jpeg':
          return false
        default:
          return 'Formato de imagem não suportado. Verifique as extensões aceitas e tente novamente.'
      }
    },

    resetUploadImage() {
      this.url = this.isGiftEdition ? this.getUrlImage(this.gift) : null
      this.gift = Object.assign({}, this.gift, {
        img_tmp: null,
      })
    },

    uploadGiftImage() {
      const giftsApi = new GiftsApi(this.logonToken)
      const formData = new FormData()
      formData.append('file', this.fileImgUploaded)

      if (this.fileImgUploaded) {
        this.isLoadingSaving = true
        this.url = URL.createObjectURL(this.fileImgUploaded)
        return giftsApi
          .uploadGiftImage(formData)
          .then((response) => {
            if (!response.data) throw 'error on upload'
            this.gift = Object.assign({}, this.gift, {
              img_tmp: response.data.tmp_file,
            })
          })
          .catch((error) => {
            console.error(error)
            this.resetUploadImage()
          })
          .finally(() => (this.isLoadingSaving = false))
      }

      this.resetUploadImage()
    },

    submit() {
      this.$validate().then((success) => {
        if (success) this.addGiftCustom()
      })
    },

    addGiftCustom() {
      this.isLoadingSaving = true
      const giftsApi = new GiftsApi(this.logonToken)
      if (this.gift.id) {
        giftsApi
          .editGift(this.gift)
          .then(() => {
            this.fetchGiftsList()
            localStorage.setItem('drawer-draft', false)
            this.gift = Object.assign({}, this.gift, EMPTY_GIFT)
            this.$parent.close()
          })
          .catch((error) => {
            console.error(error)
            this.notificationAlert = {
              message: 'Erro ao editar o presente. Por favor, tente novamente.',
              class: 'is-error',
              show: true,
            }
          })
          .finally(() => (this.isLoadingSaving = false))
      } else {
        giftsApi
          .addGiftCustom(this.gift)
          .then(() => {
            this.notificationAlert = {
              message: 'Presente criado com sucesso.',
              show: true,
            }
            this.fetchGiftsList()
            localStorage.setItem('drawer-draft', false)
            this.gift = Object.assign({}, this.gift, EMPTY_GIFT)
            this.$parent.close()
          })
          .catch((error) => {
            console.error(error)
            this.notificationAlert = {
              message: 'Erro ao criar o presente. Por favor, tente novamente.',
              class: 'is-error',
              show: true,
            }
          })
          .finally(() => (this.isLoadingSaving = false))
      }
    },
  },
}
</script>

<style lang="scss" scoped>
::-webkit-scrollbar {
  width: 10px;
}

.error-validator {
  display: none;
  color: $error;
  padding-left: 5px;
  font-size: 12px;
}

.input-validator {
  display: flex;
  justify-content: space-between;
}

.input-error {
  display: flex;
  justify-content: space-between;
}

.file-upload-csr {
  .file-label {
    overflow: inherit;

    .file-cta-csr {
      .gift-image:hover {
        background: rgb(231, 247, 248) !important;
      }

      .edit-gift-image {
        width: 44px;
        height: 32px;
        margin-left: 136px;
        margin-top: -10px;
        position: absolute;
      }

      .edit-gift-image:hover {
        width: 48px;
        height: 36px;
      }
    }
  }
}

.help {
  display: none !important;
}

.img-upload-section {
  text-align: center;
  cursor: pointer;

  img {
    width: 148px;
    height: 148px;
    border-radius: 16px;

    &.gift-image {
      object-fit: cover;
    }
  }

  @media (max-width: 1024px) {
    display: block;
    text-align: center;
    justify-content: center;
    width: auto;
  }
}

.img-upload-subtitle {
  margin-top: 24px;
  color: $neutral;
  font-size: 12px;
  font-weight: 400;
}

.content-section {
  margin-top: 20px;

  @media (max-width: 1024px) {
    text-align: left;
    justify-content: center;
  }

  .input-row {
    margin-top: 24px;
    width: 100%;

    .input-label {
      color: $neutral;
      font-size: 16px;
      font-weight: 700;
    }

    .input-gift {
      margin-top: 8px;
      width: 100%;
      height: 48px;
      border-radius: 16px;
      border: 1px solid $neutrallight;
      padding-left: 12px;
    }

    .input-validator {
      margin-top: 4px;
      color: $neutral;
      font-size: 12px;
      line-height: 14.4px;
      font-weight: 400;
      padding-left: 5px;
    }

    .input-error {
      color: $error;
    }
  }
}
</style>
